import React, { useContext, useRef, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, FormGroup, Label } from 'reactstrap';
import { Context as AuthContext } from '../../../../contexts/AuthContext';
import PerformanceImprovement from '../../../../services/PerformanceImprovement';
import { generateImprovementPdf } from '../../../../services/pdf/ImprovementPdfService';
import { toast } from 'react-toastify';

interface FinalizePerformanceImprovementModalProps {
  isOpen: boolean;
  toggle: () => void;
  recordId: string;
  record?: any;
  onSuccess: () => void;
}

const FinalizePerformanceImprovementModal: React.FC<FinalizePerformanceImprovementModalProps> = ({
  isOpen,
  toggle,
  recordId,
  record,
  onSuccess,
}) => {
  const { token, user, currentSchool }: any = useContext(AuthContext);
  const fileRef = useRef(null as any);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setSelectedFile(file || null);
  };

  const handleDownloadTemplate = async () => {
    setDownloading(true);
    try {
      if (record && user && currentSchool) {
        const safeRecord = { ...record, privateNotes: undefined };
        const { url, fileName } = await generateImprovementPdf(safeRecord, user, currentSchool);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      } else {
        const blob = await PerformanceImprovement(token).getTemplateDocument(recordId);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'Performance_Improvement.pdf');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Failed to download template.';
      toast.error(message);
    } finally {
      setDownloading(false);
    }
  };

  const showOpenFileDialog = () => {
    if (fileRef?.current?.click) {
      fileRef.current.click();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile) {
      toast.error('Please upload the signed document.');
      return;
    }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('document', selectedFile);
      await PerformanceImprovement(token).finalize(recordId, formData);
      toast.success('Performance Improvement finalized successfully.');
      setSelectedFile(null);
      toggle();
      onSuccess();
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Failed to finalize Performance Improvement.';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="md" centered>
      <ModalHeader toggle={handleClose}>Finalize Performance Improvement</ModalHeader>
      <form onSubmit={handleSubmit}>
        <ModalBody>
          <p className="text-muted mb-3">
            Download the Performance Improvement document, print it, have it signed, then upload the signed document
            below to complete the process.
          </p>
          <FormGroup>
            <Label>
              <Button
                type="button"
                color="link"
                className="p-0 font-weight-bold text-primary"
                onClick={handleDownloadTemplate}
                disabled={downloading}
              >
                <i className="fas fa-download mr-2" />
                {downloading ? 'Downloading...' : 'Performance Improvement (PDF)'}
              </Button>
            </Label>
          </FormGroup>
          <FormGroup>
            <Button
              className="btn-icon btn-sm"
              color="secondary"
              type="button"
              id="input-attach-pi"
              onClick={showOpenFileDialog}
              style={{ marginBottom: '2px' }}
            >
              <span className="btn-inner--icon pt-1" style={{ marginRight: '1px' }}>
                <i className="fas fa-paperclip" style={{ top: '0' }} />
              </span>
            </Button>
            <small className="text-muted mt-1">
              {selectedFile?.name || 'Upload the signed document (PDF or image) *'}
            </small>
            <input
              id="signed-document-pi"
              type="file"
              ref={fileRef}
              style={{ display: 'none' }}
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handleFileChange}
              required
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button type="button" color="secondary" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" color="success" disabled={submitting || !selectedFile}>
            {submitting ? 'Submitting...' : 'Finalize'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default FinalizePerformanceImprovementModal;
